import AppConfig from '../config/AppConfig';
import { ReactNode, useContext, useEffect } from 'react';
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { AuthContext } from '@urbanx/agx.auth.client';
import { AgenciesApiProvider } from '@urbanx/agx.agencies.client';
import LogRocket from 'logrocket';
import { SetupNotificationsLayout } from 'layout/SetupNotificationsLayout';
import { CampaignsApiProvider } from '@urbanx/agx.campaigns.client';
import { RpDataApiProvider } from '@urbanx/agx.rpdata.client';
import { CampaignFlowApiProvider } from '@urbanx/agx.campaignflow.client';
import { DomainApiProvider } from '@urbanx/agx.domain.client';
import { MarketingApiProvider } from '@urbanx/agx.marketing.client';
import { PoolsApiProvider } from '@urbanx/agx.pools.client';
import { RealEstateApiProvider } from '@urbanx/agx.realestate.client';
import { RexApiProvider } from '@urbanx/agx.rex.client';
import { ActivitiesApiProvider } from '@urbanx/agx.activities.client';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
      retryDelay: 3000,
      networkMode: 'always',
    },
  },
  queryCache: new QueryCache({
    onError: error => {
      console.error(error);
    },
  }),
});

const ApiProvider = ({ children }: { children: ReactNode }) => {
  const { userAccount } = useContext(AuthContext);

  useEffect(() => {
    LogRocket.identify(userAccount.agent || '', {
      name: userAccount.fullName || '',
      email: userAccount.agent || '',
      agency: userAccount.agency || '',
    });
  }, [userAccount]);

  return (
    <QueryClientProvider client={queryClient}>
      <AgenciesApiProvider appConfig={AppConfig}>
        <CampaignsApiProvider appConfig={AppConfig}>
          <RpDataApiProvider appConfig={AppConfig}>
            <CampaignFlowApiProvider appConfig={AppConfig}>
              <DomainApiProvider appConfig={AppConfig}>
                <MarketingApiProvider appConfig={AppConfig}>
                  <PoolsApiProvider appConfig={AppConfig}>
                    <RealEstateApiProvider appConfig={AppConfig}>
                      <RexApiProvider appConfig={AppConfig}>
                        <ActivitiesApiProvider appConfig={AppConfig}>
                          <SetupNotificationsLayout>
                            {children}
                          </SetupNotificationsLayout>
                        </ActivitiesApiProvider>
                      </RexApiProvider>
                    </RealEstateApiProvider>
                  </PoolsApiProvider>
                </MarketingApiProvider>
              </DomainApiProvider>
            </CampaignFlowApiProvider>
          </RpDataApiProvider>
        </CampaignsApiProvider>
      </AgenciesApiProvider>
    </QueryClientProvider>
  );
};

export default ApiProvider;
